/**
 * Code-based route tree. Routes are declared here and pass their params to page
 * components as plain props, which keeps every feature independent of routing.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { AppLayout } from '../components/layout/AppLayout';
import { ProjectLayout } from '../components/layout/ProjectLayout';
import { meQueryOptions } from '../features/auth/api';
import { LoginPage } from '../features/auth/LoginPage';
import { ChecklistDetailPage } from '../features/checklists/ChecklistDetailPage';
import { ChecklistsPage } from '../features/checklists/ChecklistsPage';
import { AccountDetailPage } from '../features/automation/accounts/AccountDetailPage';
import { AccountsPage } from '../features/automation/accounts/AccountsPage';
import { AutomationPage } from '../features/automation/AutomationPage';
import { ProjectPage as AutomationProjectPage } from '../features/automation/project/ProjectPage';
import { TeamPage as AutomationTeamPage } from '../features/automation/project/TeamPage';
import { CreateRunPage } from '../features/automation/runs/CreateRunPage';
import { DeploysPage } from '../features/automation/deploys/DeploysPage';
import { FullRunPage } from '../features/automation/runs/FullRunPage';
import { RunPage as AutomationRunPage } from '../features/automation/runs/RunPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { ProjectOverviewPage } from '../features/projects/ProjectOverviewPage';
import { ProjectSettingsPage } from '../features/projects/ProjectSettingsPage';
import { ProjectsPage } from '../features/projects/ProjectsPage';
import { TestCaseDetailPage } from '../features/test-cases/TestCaseDetailPage';
import { TestCaseFormPage } from '../features/test-cases/TestCaseFormPage';
import { TestCasesPage } from '../features/test-cases/TestCasesPage';
import { RunDetailPage } from '../features/test-runs/RunDetailPage';
import { TestRunsPage } from '../features/test-runs/TestRunsPage';
import { UsersPage } from '../features/users/UsersPage';
import { NotFoundPage } from './NotFoundPage';
import { RouteErrorPage } from './RouteErrorPage';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error }) => <RouteErrorPage error={error} />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { next?: string; error?: string } => ({
    next: typeof search.next === 'string' ? search.next : undefined,
    // set by the GitLab callback when sign-in was refused (gitlab_<reason>)
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: LoginPage,
});

/**
 * Everything below this route requires a session. The check runs before the
 * shell renders, so a signed-out visitor never sees a flash of the app.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQueryOptions);
    } catch {
      throw redirect({ to: '/login', search: { next: location.href } });
    }
  },
  component: AppLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  component: UsersPage,
});

const automationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation',
  // the catalogue by team is the default; ?view=repos — the repositories as TestRun had them
  validateSearch: (search: Record<string, unknown>): { view?: 'repos' } => ({
    view: search.view === 'repos' ? 'repos' : undefined,
  }),
  component: function AutomationRouteComponent() {
    const { view } = automationRoute.useSearch();
    return <AutomationPage view={view ?? 'catalog'} />;
  },
});

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const automationTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/teams/$teamId',
  component: function AutomationTeamRouteComponent() {
    const { teamId } = automationTeamRoute.useParams();
    return <AutomationTeamPage teamId={teamId} />;
  },
});

const automationProjectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/teams/$teamId/projects/$projectId',
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: optionalString(search.tab),
  }),
  component: function AutomationProjectRouteComponent() {
    const { teamId, projectId } = automationProjectRoute.useParams();
    const { tab } = automationProjectRoute.useSearch();
    return <AutomationProjectPage teamId={teamId} projectId={projectId} tab={tab ?? 'dashboard'} />;
  },
});

const stringList = (value: unknown): string[] | undefined => {
  const list = (Array.isArray(value) ? value : [value]).filter(
    (item): item is string => typeof item === 'string' && item !== '',
  );
  return list.length ? list : undefined;
};

const automationNewRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/runs/new',
  // a suite, classes, tags or a segment chosen on the project page come preselected
  validateSearch: (
    search: Record<string, unknown>,
  ): { project?: string; suite?: string; class?: string[]; tag?: string[]; segment?: string } => ({
    project: optionalString(search.project),
    suite: optionalString(search.suite),
    class: stringList(search.class),
    tag: stringList(search.tag),
    segment: optionalString(search.segment),
  }),
  component: function AutomationNewRunRouteComponent() {
    const { project } = automationNewRunRoute.useSearch();
    return <CreateRunPage projectId={project ?? ''} />;
  },
});

const automationRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/runs/$runId',
  // a pipeline GitLab started by itself is not recorded in TMS: the page names its project
  validateSearch: (search: Record<string, unknown>): { project?: string } => ({
    project: optionalString(search.project),
  }),
  component: function AutomationRunRouteComponent() {
    const { runId } = automationRunRoute.useParams();
    const { project } = automationRunRoute.useSearch();
    return <AutomationRunPage runId={runId} {...(project ? { projectHint: project } : {})} />;
  },
});

const automationFullRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/full-run',
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: optionalString(search.id),
  }),
  component: function AutomationFullRunRouteComponent() {
    const { id } = automationFullRunRoute.useSearch();
    return <FullRunPage {...(id ? { fullRunId: id } : {})} />;
  },
});

const automationDeploysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/deploys',
  component: DeploysPage,
});

const automationAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/accounts',
  component: AccountsPage,
});

const automationAccountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automation/accounts/$accountId',
  component: function AutomationAccountRouteComponent() {
    const { accountId } = automationAccountRoute.useParams();
    return <AccountDetailPage accountId={accountId} />;
  },
});

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects',
  component: ProjectsPage,
});

const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/$projectCode',
  component: function ProjectRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <ProjectLayout projectCode={projectCode} />;
  },
});

const projectIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/projects/$projectCode/overview',
      params: { projectCode: params.projectCode },
    });
  },
});

const projectOverviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/overview',
  component: function OverviewRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <ProjectOverviewPage projectCode={projectCode} />;
  },
});

const testCasesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-cases',
  component: function TestCasesRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <TestCasesPage projectCode={projectCode} />;
  },
});

const testCaseNewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-cases/new',
  component: function TestCaseNewRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <TestCaseFormPage projectCode={projectCode} />;
  },
});

const testCaseDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-cases/$caseKey',
  component: function TestCaseDetailRouteComponent() {
    const { projectCode, caseKey } = testCaseDetailRoute.useParams();
    return <TestCaseDetailPage projectCode={projectCode} caseKey={caseKey} />;
  },
});

const testCaseEditRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-cases/$caseKey/edit',
  component: function TestCaseEditRouteComponent() {
    const { projectCode, caseKey } = testCaseEditRoute.useParams();
    return <TestCaseFormPage projectCode={projectCode} caseKey={caseKey} />;
  },
});

const checklistsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/checklists',
  component: function ChecklistsRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <ChecklistsPage projectCode={projectCode} />;
  },
});

const checklistDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/checklists/$checklistKey',
  component: function ChecklistDetailRouteComponent() {
    const { projectCode, checklistKey } = checklistDetailRoute.useParams();
    return <ChecklistDetailPage projectCode={projectCode} checklistKey={checklistKey} />;
  },
});

const testRunsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-runs',
  component: function TestRunsRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <TestRunsPage projectCode={projectCode} />;
  },
});

const testRunDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/test-runs/$runKey',
  component: function TestRunDetailRouteComponent() {
    const { projectCode, runKey } = testRunDetailRoute.useParams();
    return <RunDetailPage projectCode={projectCode} runKey={runKey} />;
  },
});

const projectSettingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/settings',
  component: function ProjectSettingsRouteComponent() {
    const { projectCode } = projectRoute.useParams();
    return <ProjectSettingsPage projectCode={projectCode} />;
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    dashboardRoute,
    usersRoute,
    automationRoute,
    automationTeamRoute,
    automationProjectRoute,
    automationNewRunRoute,
    automationRunRoute,
    automationFullRunRoute,
    automationDeploysRoute,
    automationAccountsRoute,
    automationAccountRoute,
    projectsRoute,
    projectRoute.addChildren([
      projectIndexRoute,
      projectOverviewRoute,
      testCasesRoute,
      testCaseNewRoute,
      testCaseDetailRoute,
      testCaseEditRoute,
      checklistsRoute,
      checklistDetailRoute,
      testRunsRoute,
      testRunDetailRoute,
      projectSettingsRoute,
    ]),
  ]),
]);

/** `history` is injected by tests so the real route tree can run in jsdom. */
export const createAppRouter = (queryClient: QueryClient, history?: RouterHistory) =>
  createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
  });

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
